import { useEffect, useState } from "react";
import { api, apiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Plus } from "lucide-react";

export default function Produkte() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", brand: "", model: "", ean: "", category_id: "", description: "", specs: {} });

  const load = () => api.get(`/products?q=${encodeURIComponent(q)}`).then((r) => setProducts(r.data));
  useEffect(() => { load(); api.get("/categories").then((r) => setCategories(r.data)); }, []);

  const category = categories.find((c) => c.id === form.category_id);

  const save = async () => {
    try {
      const { data } = await api.post("/products", { ...form, category_name: category?.name || "" });
      if (data.duplicates?.length) toast.warning(`Hinweis: ${data.duplicates.length} mögliche Dublette(n) gefunden.`);
      else toast.success("Produkt erstellt.");
      setOpen(false);
      setForm({ name: "", brand: "", model: "", ean: "", category_id: "", description: "", specs: {} });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="produkte-page">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-zinc-400">Artikel</div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mt-1">Produkte</h1>
          <p className="text-sm text-zinc-500 mt-1">Produkt = allgemeines Modell. Konkrete Einheiten sind Artikel im Bestand.</p>
        </div>
        <div className="flex gap-3">
          <Input placeholder="Suchen…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="w-56" data-testid="products-search" />
          <Button onClick={() => setOpen(true)} className="bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="new-product-button"><Plus className="w-4 h-4 mr-1" /> Neues Produkt</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {products.length === 0 && <div className="col-span-full bg-white border border-dashed border-zinc-300 rounded-xl p-10 text-center text-sm text-zinc-400">Noch keine Produkte. Tipp: Nutzen Sie den KI-Import.</div>}
        {products.map((p) => (
          <div key={p.id} className="bg-white border border-zinc-200 rounded-xl p-5 shadow-sm hover:border-zinc-400 transition-colors" data-testid={`product-card-${p.id}`}>
            <div className="font-display font-medium">{p.name}</div>
            <div className="text-xs text-zinc-500 mt-1">{p.brand} {p.model}</div>
            <div className="flex justify-between items-center mt-3">
              <span className="text-[10px] font-mono bg-zinc-100 border border-zinc-200 px-2 py-0.5 rounded-full">{p.category_name || "Ohne Kategorie"}</span>
              <span className="font-mono text-xs text-zinc-400">{p.ean || ""}</span>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="product-form-dialog">
          <DialogHeader><DialogTitle>Neues Produkt</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-form-name" className="mt-1.5" /></div>
            <div>
              <Label>Kategorie (lädt Modul-Felder)</Label>
              <select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value, specs: {} })} data-testid="product-form-category"
                className="mt-1.5 w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm bg-white">
                <option value="">— wählen —</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Hersteller</Label><Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Modell</Label><Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="mt-1.5" /></div>
              <div className="col-span-2"><Label>EAN</Label><Input value={form.ean} onChange={(e) => setForm({ ...form, ean: e.target.value })} className="mt-1.5 font-mono" /></div>
            </div>
            {category && (
              <div className="border border-zinc-100 rounded-lg p-4 bg-zinc-50/50" data-testid="product-dynamic-fields">
                <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 mb-3">Modul: {category.name}</div>
                <div className="grid grid-cols-2 gap-3">
                  {category.fields.filter((f) => !["brand", "model", "ean"].includes(f.name)).map((f) => (
                    <div key={f.name}>
                      <Label>{f.label}</Label>
                      {f.type === "select" ? (
                        <select value={form.specs[f.name] || ""} onChange={(e) => setForm({ ...form, specs: { ...form.specs, [f.name]: e.target.value } })}
                          className="mt-1.5 w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm bg-white">
                          <option value="">—</option>
                          {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      ) : (
                        <Input value={form.specs[f.name] || ""} onChange={(e) => setForm({ ...form, specs: { ...form.specs, [f.name]: e.target.value } })} className="mt-1.5" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div><Label>Beschreibung</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" /></div>
            <Button onClick={save} disabled={!form.name} className="w-full bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="product-save-button">Produkt speichern</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
